import { CashPool, CashPoolFormData } from '@/types/cash-pool.types';

/**
 * Find an ACTIVE pool that already carries the same identity as the form
 * (counterparty × product × currency × rate definition).
 *
 * Duplicates are legal - product answer #13 asks only for a warning naming the
 * existing pool and its conditions, and lets the trader create the twin anyway.
 * The check runs client-side against the pool list the Collateral Manager has
 * already loaded: no extra endpoint, and a lost race merely means the advisory
 * banner doesn't appear.
 */
export const findDuplicatePool = (
  pools: CashPool[],
  form: CashPoolFormData,
  excludeId?: number
): CashPool | undefined => {
  if (form.counterparty == null) return undefined;
  const currency = form.currency.trim().toUpperCase();
  if (!currency) return undefined;

  return pools.find((pool) => {
    if (!pool.is_active) return false;
    if (pool.id === excludeId) return false;
    if (pool.counterparty !== form.counterparty) return false;
    if (pool.product !== form.product) return false;
    if (pool.currency.toUpperCase() !== currency) return false;
    if (pool.rate_type !== form.rate_type) return false;

    if (form.rate_type === 'floating') {
      const spread = Number(form.spread_bps);
      if (form.spread_bps.trim() === '' || Number.isNaN(spread)) return false;
      return (
        pool.benchmark_name === form.benchmark_name &&
        pool.spread_bps === spread
      );
    }

    // Compare numerically: "4.5" from the input and "4.5000" from the API are
    // the same rate.
    const rate = Number(form.fixed_rate);
    if (form.fixed_rate.trim() === '' || Number.isNaN(rate)) return false;
    return pool.fixed_rate != null && Number(pool.fixed_rate) === rate;
  });
};

/** One-line description of a pool's terms, for the duplicate warning. */
export const describePoolTerms = (pool: CashPool): string =>
  `${pool.currency} · ${pool.rate_definition} · ACT/${pool.day_count_convention}`;
